import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Boxes, Warehouse } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { InventoryLevel } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ImportExportLinks } from '@/features/imports-exports';
import { useStore } from '@/shared/store/store-context';

import { AdjustStockDialog } from '../components/adjust-stock-dialog';
import { useInventoryColumns, type LevelAction } from '../components/inventory-columns';
import { inventoryFilters } from '../components/inventory-filters';
import { MovementsSheet } from '../components/movements-sheet';
import { ReorderPointDialog } from '../components/reorder-point-dialog';
import { useInventoryLevels } from '../hooks/use-inventory-levels';
import { useStockLocations } from '../hooks/use-stock-locations';

const ROUTE_ID = '/_authed/_active/s/$storeId/inventory' as const;
const ROUTE_PATH = '/s/$storeId/inventory' as const;

type Dialog =
  | { kind: 'none' }
  | { kind: 'adjust'; level: InventoryLevel }
  | { kind: 'reorderPoint'; level: InventoryLevel };

/** A variant has one row per location once several locations are listed. */
const levelId = (level: InventoryLevel) => `${level.variantId}:${level.locationId}`;

function Inventory() {
  const { t } = useTranslation('inventory');
  const store = useStore();
  const queryClient = useQueryClient();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const [ledger, setLedger] = useState<InventoryLevel | null>(null);

  const filters = useMemo(
    () => inventoryFilters({ t, queryClient, storeId: store.id }),
    [queryClient, store.id, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const locations = useStockLocations(store.id);
  const query = useInventoryLevels(store.id, list.params);

  const onAction = useCallback((action: LevelAction, level: InventoryLevel) => {
    if (action === 'movements') setLedger(level);
    else setDialog({ kind: action, level });
  }, []);
  const columns = useInventoryColumns(onAction, locations.data ?? []);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: levelId,
    tableId: 'inventory',
  });

  const locationOf = (level: InventoryLevel | null) =>
    level ? locations.data?.find((l) => l.id === level.locationId) : undefined;
  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;
  const closeDialog = (open: boolean) => {
    if (!open) setDialog({ kind: 'none' });
  };

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={
          <>
            <ImportExportLinks storeId={store.id} kind="inventory_csv" />
            <Can permission="inventory.read">
              <Button asChild variant="outline" data-tour="inventory.levels.locations-button">
                <Link to="/s/$storeId/settings/locations" params={{ storeId: store.id }}>
                  <Warehouse aria-hidden />
                  {t('locations.manage')}
                </Link>
              </Button>
            </Can>
          </>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={Boxes}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="inventory.levels.empty"
          tourLink
        />
      ) : (
        <DataTable
          pillFilter="lowStock"
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          getRowLabel={(row) => row.sku ?? row.variantTitle}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/inventory/$variantId',
              params: { storeId: store.id, variantId: row.variantId },
            })
          }
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'inventory.levels.table', filters: 'inventory.levels.filters' }}
        />
      )}
      {dialog.kind === 'adjust' ? (
        <AdjustStockDialog
          key={levelId(dialog.level)}
          storeId={store.id}
          level={dialog.level}
          location={locationOf(dialog.level)}
          open
          onOpenChange={closeDialog}
        />
      ) : null}
      {dialog.kind === 'reorderPoint' ? (
        <ReorderPointDialog
          key={levelId(dialog.level)}
          storeId={store.id}
          level={dialog.level}
          open
          onOpenChange={closeDialog}
        />
      ) : null}
      <MovementsSheet
        storeId={store.id}
        level={ledger}
        locationName={locationOf(ledger)?.name}
        onClose={() => setLedger(null)}
      />
    </PageContent>
  );
}

export function InventoryPage() {
  return (
    <FeatureErrorBoundary name="inventory.levels">
      <Inventory />
    </FeatureErrorBoundary>
  );
}
